import React, { useState, useEffect } from 'react';
import { getTransactionHistory, createRefund } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import { toast } from 'react-toastify';
import Loader from '../UI/Loader/Loader';
import moment from 'moment';

const TransactionHistory = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const { userData } = useUserContext();

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const res = await getTransactionHistory(userData.token, userData?.selectedOrganization?.org_token);
                if (res.status) {
                    setTransactions(res.data);
                }
            } catch (error) {
                console.error("Error fetching transactions:", error);
                toast.error("Failed to load transaction history.");
            } finally {
                setLoading(false);
            }
        };

        if (userData?.selectedOrganization?.org_token) {
            fetchHistory();
        }
    }, [userData]);

    const handleRefund = async (paymentIntentId) => {
        if (!window.confirm("Are you sure you want to request a refund for this transaction?")) return;

        try {
            const res = await createRefund(userData.token, userData?.selectedOrganization?.org_token, {
                paymentIntent: paymentIntentId
            });
            if (res.status) {
                toast.success("Refund request submitted successfully!");
                // Refresh history
                const updatedRes = await getTransactionHistory(userData.token, userData?.selectedOrganization?.org_token);
                if (updatedRes.status) setTransactions(updatedRes.data);
            }
        } catch (error) {
            console.error("Refund error:", error);
            toast.error(error.message || "Failed to process refund.");
        }
    };

    if (loading) return <Loader message="Loading transactions..." />;

    if (transactions.length === 0) {
        return (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <p className="text-gray-500">No transactions found.</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-800">Billing History</h3>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {transactions.map((tx) => {
                            const isTopup = tx.object === 'payment_intent';
                            const paymentIntentId = isTopup ? tx.id : tx.payment_intent;

                            return (
                                <tr key={tx.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {moment.unix(tx.created).format('MMM D, YYYY')}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                        {(tx.amount_paid / 100).toFixed(2)} {tx.currency.toUpperCase()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${tx.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                            {tx.status.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {tx.invoice_pdf ? (
                                            <a href={tx.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                Download PDF
                                            </a>
                                        ) : (
                                            <span className="text-gray-400 text-xs">N/A</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {paymentIntentId && tx.status === 'paid' && (
                                            <button
                                                onClick={() => handleRefund(paymentIntentId)}
                                                className="text-red-600 hover:text-red-900 font-medium"
                                            >
                                                Request Refund
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TransactionHistory;
